import { publishToCloudflare, saveCfCreds, showPublishModal } from '../api.js';
import { prepareSiteFiles } from '../build/export.js';
import { initSection } from '../build/helpers.js';
import { analyzePage, suggestPages } from '../build/page-analyzer.js';
import { buildAllPages, buildOne, regenSect, startBuildAll } from '../build/pipeline.js';
import { DI, DW, SECT_TYPES, TC } from '../constants.js';
import { $, assembled, createTabSwitcher, mk, showErr, writeFrame } from '../dom.js';
import { createPage } from '../logic/pages.ts';
import {
  calcExpandedAfterRemove,
  getAvailableTypes,
  getProgressInfo,
  getStatusText,
} from '../logic/sections.ts';
import { curPage, ST, sectionId } from '../state.js';
import { parseVideoUrl } from '../video.js';
import { captureSection, renderChat } from './chat.js';

// ── BUILD SCREEN (merged Sections + Edit) ────────────────────────
// Section list, page nav, section add/remove, shell row, tabs, chat, export.

// ── PAGE MANAGEMENT LOGIC (extract to logic/pages.js during Vue migration) ──
// addPage(), switchPage(), removePage(), addSection(), removeSection()
// Pure state manipulation — direct migration to Pinia actions.

// ── ADD PAGE DROPDOWN ────────────────────────────────────────────

let AddPageDropdownOpen = false;

/** Show the add-page dropdown with AI suggest, From URL, Blank page options. */
function showAddPageDropdown() {
  AddPageDropdownOpen = !AddPageDropdownOpen;
  renderAddPageDropdown();
}

/** Render the add-page dropdown. */
function renderAddPageDropdown() {
  let dd = $('add-page-dropdown');
  if (!AddPageDropdownOpen) {
    if (dd) {
      dd.remove();
    }
    return;
  }
  if (!dd) {
    dd = mk('div', { class: 'add-page-dropdown', id: 'add-page-dropdown' });
    $('page-nav')?.append(dd);
  }
  dd.innerHTML = '';

  dd.append(
    mk(
      'div',
      {
        class: 'add-page-option',
        onclick() {
          AddPageDropdownOpen = false;
          renderAddPageDropdown();
          void handleAiSuggest();
        },
      },
      '\u{1F4E1} AI suggest',
    ),
    mk(
      'div',
      {
        class: 'add-page-option',
        onclick() {
          AddPageDropdownOpen = false;
          renderAddPageDropdown();
          handleFromUrl();
        },
      },
      '\u{1F517} From URL',
    ),
    mk(
      'div',
      {
        class: 'add-page-option',
        onclick() {
          AddPageDropdownOpen = false;
          renderAddPageDropdown();
          handleBlankPage();
        },
      },
      '\u{1F4C4} Blank page',
    ),
  );

  // Close on outside click
  const closeHandler = e => {
    if (!(e.target.closest('#add-page-dropdown') || e.target.closest('.page-add-btn'))) {
      AddPageDropdownOpen = false;
      renderAddPageDropdown();
      document.removeEventListener('click', closeHandler);
    }
  };
  setTimeout(() => document.addEventListener('click', closeHandler), 0);
}

/** AI suggest: call suggest-pages, show results, let user pick. */
async function handleAiSuggest() {
  // Remove any existing dropdown first
  $('add-page-dropdown')?.remove();
  const dd = mk('div', { class: 'add-page-dropdown', id: 'add-page-dropdown' });
  dd.append(mk('div', { class: 'add-page-loading' }, 'Suggesting pages\u2026'));
  $('page-nav')?.append(dd);

  try {
    const suggested = await suggestPages();
    dd.innerHTML = '';
    if (!suggested?.length) {
      dd.append(mk('div', { class: 'add-page-loading' }, 'No suggestions available'));
      setTimeout(() => dd.remove(), 2000);
      return;
    }
    for (const s of suggested) {
      const priorityClass =
        s.priority === 'essential'
          ? 'badge-essential'
          : s.priority === 'recommended'
            ? 'badge-recommended'
            : 'badge-optional';
      const row = mk('div', {
        class: 'add-page-suggest-row',
        onclick() {
          addPageByName(s.name);
          dd.remove();
        },
      });
      row.append(
        mk('span', { class: 'suggest-name' }, s.name),
        mk('span', { class: `suggest-badge ${priorityClass}` }, s.priority),
        mk('span', { class: 'suggest-purpose' }, s.purpose),
      );
      dd.append(row);
    }
  } catch (e) {
    dd.innerHTML = '';
    dd.append(mk('div', { class: 'add-page-loading' }, `Failed: ${e.message}`));
    setTimeout(() => dd.remove(), 3000);
  }
}

/** From URL: prompt for URL, create page with sourceUrl. */
function handleFromUrl() {
  const url = prompt('Enter the reference page URL:');
  if (!url?.trim()) {
    return;
  }
  let name;
  try {
    const parsed = new URL(url.trim());
    const slug =
      parsed.pathname
        .replace(/^\/+|\/+$/g, '')
        .split('/')
        .pop() || '';
    name = slug ? slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'New Page';
  } catch {
    name = 'New Page';
  }
  const newPageObj = createPage(name, ST.pages, sectionId);
  if (newPageObj) {
    newPageObj.sourceUrl = url.trim();
    newPageObj.pageMode = undefined;
    newPageObj.analyzed = false;
    ST.pages.push(newPageObj);
    ST.activePage = ST.pages.length - 1;
    ST.expanded = null;
    renderBuild();
  }
}

/** Blank page: prompt for name, create empty page (current behavior). */
function handleBlankPage() {
  addPageByName();
}

/** Add a page by name (prompt if not given). */
function addPageByName(name) {
  if (!name) {
    name = prompt('Page name (e.g. "About", "Pricing"):', '');
  }
  if (!name?.trim()) {
    return;
  }
  const newPageObj = createPage(name, ST.pages, sectionId);
  if (newPageObj) {
    ST.pages.push(newPageObj);
    ST.activePage = ST.pages.length - 1;
    ST.expanded = null;
    renderBuild();
  }
}

/**
 * Ensure a non-home page's shared header/footer are in sync with home.
 * Covers timing gaps when pages are created before home sections are built.
 */
function syncSharedFromHome(page) {
  if (!page || page.id === 'page-home') {
    return;
  }
  const home = ST.pages[0];
  if (!home) {
    return;
  }
  for (const type of ['header', 'footer']) {
    const src = home.sects.find(s => s.type === type);
    const tgt = page.sects.find(s => s.type === type);
    if (src?.html && tgt && tgt.html !== src.html) {
      tgt.html = src.html;
      tgt.status = src.status;
      tgt.error = '';
    }
  }
}

/** @param {number} idx - Page index to switch to */
function switchPage(idx) {
  if (idx === ST.activePage) {
    return;
  }
  ST.activePage = idx;
  ST.expanded = null;
  syncSharedFromHome(curPage());
  renderBuild();
  if (curPage().assembled) {
    writeFrame('ifr-build', curPage().assembled);
  } else {
    $('ifr-build').srcdoc =
      '<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#888;font-size:14px">Page not built yet</body></html>';
  }
}

/** @param {number} idx - Page index to remove */
function removePage(idx, e) {
  e?.stopPropagation();
  if (ST.pages.length <= 1) {
    showErr('Cannot remove the only page');
    return;
  }
  if (!confirm(`Remove page "${ST.pages[idx].name}"? This cannot be undone.`)) {
    return;
  }
  ST.pages.splice(idx, 1);
  ST.activePage = Math.min(ST.activePage, ST.pages.length - 1);
  ST.expanded = null;
  renderBuild();
  if (curPage().assembled) {
    writeFrame('ifr-build', curPage().assembled);
  }
}

/** @param {string} typeId - Section type ID (e.g. 'hero', 'features') */
function addSection(typeId) {
  const def = SECT_TYPES.find(t => t.id === typeId) || { id: typeId, color: '#64748b', desc: '' };
  const title = def.id.charAt(0).toUpperCase() + def.id.slice(1).replace(/-/g, ' ');
  const planned = (ST.originalConceptSections || []).find(s => s.type === typeId);
  const newSect = initSection(
    {
      type: def.id,
      title: planned?.title || title,
      purpose: planned?.purpose || def.desc,
      visualBrief: planned?.visualBrief || '',
      copyNotes: planned?.copyNotes || '',
      layout: planned?.layout || 'auto',
      bgStyle: planned?.bgStyle || 'light',
    },
    curPage().sects,
  );
  curPage().sects.push(newSect);
  // Invalidate home screenshot cache on home page changes
  if (ST.activePage === 0) {
    ST.homePageScreenshot = null;
  }
  // Sync to concept
  ST.concept.sections.push({
    id: newSect.id,
    type: newSect.type,
    title: newSect.title,
    purpose: newSect.purpose,
    visualBrief: newSect.visualBrief || '',
    layout: newSect.layout || 'auto',
    bgStyle: newSect.bgStyle || 'light',
    copyNotes: newSect.copyNotes || '',
  });
  renderSList();
  renderSbFoot();
  updSectsCtx();
  ST.pickerOpen = false;
  renderAddSectBar();
}

/** @param {number} idx - Section index to remove */
function removeSection(idx, e) {
  e?.stopPropagation();
  const sect = curPage().sects[idx];
  curPage().sects.splice(idx, 1);
  if (ST.activePage === 0) {
    ST.homePageScreenshot = null;
  }
  // Sync concept
  if (sect) {
    const cIdx = ST.concept.sections.findIndex(s => s.id === sect.id);
    if (cIdx >= 0) {
      ST.concept.sections.splice(cIdx, 1);
    }
  }
  ST.expanded = calcExpandedAfterRemove(idx, ST.expanded);
  curPage().edited = false;
  ST.assembled = assembled(true);
  if (ST.assembled) {
    writeFrame('ifr-build', ST.assembled);
  }
  renderSList();
  renderSbFoot();
  updSectsCtx();
}

// ── DOM RENDERING (becomes BuildPage.vue + components during Vue migration) ──
// renderBuild(), renderSList(), renderPageNav(), etc.

/** Render the page tab bar with add/remove controls and status indicators. */
function renderPageNav() {
  const c = $('page-nav');
  if (!c) {
    return;
  }
  c.innerHTML = '';
  const tabs = mk('div', { class: 'page-tabs' });
  ST.pages.forEach((p, i) => {
    const hasContentSections = p.sects.some(s => s.type !== 'header' && s.type !== 'footer');
    const allSectionsDone = p.sects
      .filter(s => s.type !== 'header' && s.type !== 'footer')
      .every(s => s.status === 'done');

    let indicator = '';
    if (i === 0) {
      indicator = allSectionsDone && hasContentSections ? ' \u2713' : '';
    } else if (!hasContentSections) {
      indicator = ' \u00b7';
    } else if (allSectionsDone) {
      indicator = ' \u2713';
    }

    const tab = mk(
      'button',
      {
        class: `page-tab${i === ST.activePage ? ' active' : ''}`,
        onclick() {
          switchPage(i);
        },
      },
      `${p.name}${indicator}`,
    );
    if (ST.pages.length > 1 && i > 0) {
      const x = mk(
        'button',
        {
          class: 'page-tab-close',
          title: 'Remove page',
          onclick(e) {
            removePage(i, e);
          },
        },
        '\u00d7',
      );
      tab.append(x);
    }
    tabs.append(tab);
  });
  tabs.append(
    mk(
      'button',
      {
        class: 'page-add-btn',
        title: 'Add page',
        onclick() {
          showAddPageDropdown();
        },
      },
      '\uff0b',
    ),
  );

  // Build all pages button (when multiple pages have pending sections)
  const pagesWithPending = ST.pages.filter(p =>
    p.sects.some(s => s.type !== 'header' && s.type !== 'footer' && s.status === 'pending'),
  );
  if (ST.pages.length > 1 && pagesWithPending.length > 1) {
    tabs.append(
      mk(
        'button',
        {
          class: 'page-build-all-btn',
          title: 'Build all pages',
          onclick() {
            buildAllPages();
          },
        },
        '\u25b6 Build all pages',
      ),
    );
  }

  c.append(tabs);
}

/** Render the "Add section" button and section type picker dropdown. */
function renderAddSectBar() {
  const wrap = $('add-sect-bar');
  if (!wrap) {
    return;
  }
  wrap.innerHTML = '';
  if (!wrap._bound) {
    wrap._bound = true;
    document.addEventListener('click', e => {
      if (!e.target.closest('.add-sect-wrap') && ST.pickerOpen) {
        ST.pickerOpen = false;
        renderAddSectBar();
      }
    });
  }
  if (ST.pickerOpen) {
    const picker = mk('div', { class: 'sect-picker' });
    const availTypes = getAvailableTypes(SECT_TYPES, ST.activePage > 0);
    availTypes.forEach(t => {
      const row = mk('div', {
        class: 'sect-pick-row',
        onclick() {
          addSection(t.id);
        },
      });
      row.append(
        mk(
          'span',
          { class: 'sect-pick-pill', style: `background:${t.color}22;color:${t.color}` },
          t.id,
        ),
        mk(
          'span',
          { class: 'sect-pick-name' },
          t.id.charAt(0).toUpperCase() + t.id.slice(1).replace(/-/g, ' '),
        ),
        mk('span', { class: 'sect-pick-desc' }, t.desc),
      );
      picker.append(row);
    });
    wrap.append(picker);
  }
  wrap.append(
    mk(
      'button',
      {
        class: 'add-sect-btn',
        onclick(e) {
          e.stopPropagation();
          ST.pickerOpen = !ST.pickerOpen;
          renderAddSectBar();
        },
      },
      ST.pickerOpen ? '\u2715 Cancel' : '\uff0b Add section',
    ),
  );
}

// ── BUILD SCREEN TABS ────────────────────────────────────────────

const { renderTabs: renderBuildTabs, renderContent: renderBuildTabContent } = createTabSwitcher({
  barId: 'build-tabs',
  stateKey: 'chatTab',
  tabs: [
    { id: 'sections', label: 'Sections', contentId: 'build-sects-content' },
    { id: 'chat', label: 'Chat', contentId: 'build-chat-content', render: () => renderChat('build-chat', 'ifr-build') },
  ],
});

// ── PREVIEW TOOLBAR (device switch + inline edit) ────────────────
/** Render device switcher and inline-edit toggle in the preview toolbar. */
function renderPrevToolbar() {
  const c = $('prev-toolbar-build');
  if (!c) {
    return;
  }
  c.innerHTML = '';

  // Device switcher
  const devSw = mk('div', { class: 'prev-tb-group' });
  ['desktop', 'tablet', 'mobile'].forEach(d => {
    const b = mk('button', {
      class: `dsw-btn${ST.device === d ? ' active' : ''}`,
      title: d,
      onclick() {
        ST.device = d;
        document.querySelectorAll('.dev-frame').forEach(f => {
          f.style.maxWidth = DW[d];
        });
        renderPrevToolbar();
      },
    });
    b.innerHTML = DI[d];
    devSw.append(b);
  });
  c.append(devSw);

  // Inline edit toggle
  const editBtn = mk('button', {
    class: `prev-tb-btn${ST.inlineEditMode ? ' active' : ''}`,
    title: ST.inlineEditMode ? 'Disable inline editing' : 'Enable inline editing',
    onclick() {
      ST.inlineEditMode = !ST.inlineEditMode;
      const ifr = $('ifr-build');
      if (ifr?.contentWindow) {
        ifr.contentWindow.postMessage({ type: 'sf-toggle-edit', enabled: ST.inlineEditMode }, '*');
      }
      renderPrevToolbar();
    },
  });
  editBtn.innerHTML = `\u270E<span class="prev-tb-label">${ST.inlineEditMode ? 'Editing' : 'Edit text'}</span>`;
  c.append(editBtn);
}

// ── MODE PICKER ─────────────────────────────────────────────────
/**
 * Render the mode picker UI for unplanned non-home pages.
 * Shows match/fresh/scratch options when a page has no content sections.
 */
function renderModePicker(page) {
  const c = $('slist');
  if (!c) {
    return;
  }
  c.innerHTML = '';

  const wrap = mk('div', { class: 'mode-picker' });
  wrap.append(mk('h3', { class: 'mode-picker-title' }, `How should we build "${page.name}"?`));

  const hasSource = !!page.sourceUrl;

  if (hasSource) {
    wrap.append(
      modeCard(
        'match',
        'Match source page',
        'Analyze the original page and replicate its structure with your new brand.',
        'Best for: keeping proven page layouts.',
      ),
    );
    wrap.append(
      modeCard(
        'fresh',
        'Fresh design',
        'AI creates the optimal section structure for this page based on its purpose.',
        'Best for: modernizing outdated pages.',
      ),
    );
  }
  wrap.append(
    modeCard(
      'scratch',
      'Start from scratch',
      'Empty page \u2014 add sections manually.',
      'Best for: completely new page ideas.',
    ),
  );

  c.append(wrap);

  function modeCard(mode, title, desc, hint) {
    return mk(
      'div',
      {
        class: 'mode-card',
        onclick() {
          page.pageMode = mode;
          if (mode === 'scratch') {
            renderBuild();
          } else {
            void startPageAnalysis(page);
          }
        },
      },
      mk('div', { class: 'mode-card-title' }, title),
      mk('div', { class: 'mode-card-desc' }, desc),
      mk('div', { class: 'mode-card-hint' }, hint),
    );
  }
}

/** Start per-page analysis with progress UI. */
async function startPageAnalysis(page) {
  const c = $('slist');
  if (!c) {
    return;
  }
  c.innerHTML = '';

  const wrap = mk('div', { class: 'page-analysis-progress' });
  wrap.append(mk('h3', { class: 'mode-picker-title' }, `Analyzing "${page.name}"\u2026`));

  const steps = [
    { key: 'scraping', label: 'Scraping page content' },
    { key: 'extracting', label: 'Extracting structure' },
    { key: 'planning', label: 'Planning sections' },
    { key: 'done', label: 'Complete' },
  ];
  const stepList = mk('div', { class: 'analysis-steps' });
  for (const step of steps) {
    stepList.append(
      mk(
        'div',
        { class: 'analysis-step', id: `pa-step-${step.key}` },
        mk('span', { class: 'analysis-step-dot' }, '\u25cb'),
        mk('span', {}, ` ${step.label}`),
      ),
    );
  }
  let cancelled = false;
  const skipBtn = mk(
    'button',
    {
      class: 'btn btn-g btn-sm',
      style: 'margin-top:16px',
      onclick() {
        cancelled = true;
        page.pageMode = 'scratch';
        renderBuild();
      },
    },
    'Skip \u2014 add sections manually',
  );
  wrap.append(stepList, skipBtn);
  c.append(wrap);

  try {
    const sections = await analyzePage(page, step => {
      if (cancelled) {
        return;
      }
      // Update progress UI
      for (const s of steps) {
        const el = $(`pa-step-${s.key}`);
        if (!el) {
          continue;
        }
        const dot = el.querySelector('.analysis-step-dot');
        if (s.key === step) {
          dot.textContent = '\u25cf';
          el.classList.add('active');
        } else if (steps.indexOf(s) < steps.findIndex(x => x.key === step)) {
          dot.textContent = '\u2713';
          el.classList.remove('active');
          el.classList.add('done');
        }
      }
    });

    // User may have clicked "Skip" while analysis was running
    if (cancelled) {
      return;
    }

    // Insert planned sections between header and footer
    const headerIdx = page.sects.findIndex(s => s.type === 'header');
    const insertPos = headerIdx >= 0 ? headerIdx + 1 : 0;
    page.sects.splice(insertPos, 0, ...sections);

    // Sync to concept
    for (const s of sections) {
      ST.concept.sections.push({
        id: s.id,
        type: s.type,
        title: s.title,
        purpose: s.purpose,
        visualBrief: s.visualBrief || '',
        layout: s.layout || 'auto',
        bgStyle: s.bgStyle || 'light',
        copyNotes: s.copyNotes || '',
      });
    }

    renderBuild();
  } catch (e) {
    console.error('Page analysis failed:', e);
    c.innerHTML = '';
    const errWrap = mk('div', { class: 'page-analysis-progress' });
    errWrap.append(
      mk('h3', { class: 'mode-picker-title' }, 'Analysis failed'),
      mk('p', { style: 'color:var(--txt3);font-size:13px' }, e.message),
      mk(
        'div',
        { style: 'display:flex;gap:8px;margin-top:12px' },
        mk(
          'button',
          {
            class: 'btn btn-p btn-sm',
            onclick() {
              void startPageAnalysis(page);
            },
          },
          'Retry',
        ),
        mk(
          'button',
          {
            class: 'btn btn-g btn-sm',
            onclick() {
              page.pageMode = 'scratch';
              renderBuild();
            },
          },
          'Switch to scratch',
        ),
      ),
    );
    c.append(errWrap);
  }
}

// ── BUILD SCREEN ─────────────────────────────────────────────────
/** Main build screen render — composes all sub-renderers. */
function renderBuild() {
  renderPrevToolbar();
  renderPageNav();
  updSectsCtx();

  // Sync shared header/footer from home before rendering
  const currentPage = curPage();
  const isExtraPage = ST.activePage > 0;
  if (isExtraPage) {
    syncSharedFromHome(currentPage);
  }
  const hasContentSections = currentPage.sects.some(
    s => s.type !== 'header' && s.type !== 'footer',
  );

  if (isExtraPage && !hasContentSections && !currentPage.pageMode) {
    renderModePicker(currentPage);
    renderSbFoot();
    renderBuildTabs();
    renderBuildTabContent();
    return;
  }

  renderShellRow();
  renderSList();

  // Add "change mode" link for non-home pages with a chosen mode
  if (isExtraPage && currentPage.pageMode) {
    const changeModeLink = mk(
      'a',
      {
        class: 'change-mode-link',
        href: '#',
        onclick(e) {
          e.preventDefault();
          if (!confirm('This will remove all planned sections for this page. Continue?')) {
            return;
          }
          // Remove content sections (keep header/footer)
          currentPage.sects = currentPage.sects.filter(
            s => s.type === 'header' || s.type === 'footer',
          );
          currentPage.pageMode = undefined;
          currentPage.analyzed = false;
          ST.homePageScreenshot = null;
          renderBuild();
        },
      },
      '\u2190 Change build mode',
    );
    const slist = $('slist');
    if (slist) {
      slist.prepend(changeModeLink);
    }
  }

  renderAddSectBar();
  renderSbFoot();
  renderBuildTabs();
  renderBuildTabContent();
  if (curPage().assembled) {
    setTimeout(() => {
      writeFrame('ifr-build', curPage().assembled);
    }, 40);
  }
}

/** Legacy alias so nav.js can call renderSections if needed. */
const renderSections = renderBuild;

/** Render the page shell status row (fonts, colors, reset). */
function renderShellRow() {
  const c = $('shell-row');
  if (!c) {
    return;
  }
  c.innerHTML = '';
  const st = ST.shellStatus;
  const dot = mk('div', {
    class: `sdot sd-${st === 'done' ? 'done' : st === 'building' ? 'spin' : st === 'error' ? 'err' : 'idle'}`,
  });
  if (st === 'done') {
    dot.append(mk('span', { style: 'font-size:8px;font-weight:700;color:var(--teal)' }, '\u2713'));
  }
  if (st === 'error') {
    dot.append(mk('span', { style: 'font-size:8px;font-weight:700;color:var(--red)' }, '!'));
  }
  c.append(
    dot,
    mk(
      'span',
      {
        style: `font-size:12px;font-weight:500;color:${st === 'done' ? 'var(--txt)' : 'var(--txt2)'}`,
      },
      'Page shell',
    ),
    mk(
      'span',
      { style: 'font-size:10px;color:var(--txt3);margin-left:auto' },
      'fonts \u00b7 colors \u00b7 reset',
    ),
  );
}

/** Render the section list with drag-reorder, expand/collapse, and action buttons. */
function renderSList() {
  const c = $('slist');
  if (!c) {
    return;
  }
  c.innerHTML = '';
  const frameId = 'ifr-build';
  ST.sects.forEach((s, idx) => {
    const isShared = (s.type === 'header' || s.type === 'footer') && ST.activePage > 0;
    const isExp = !isShared && ST.expanded === idx;
    const tclr = TC[s.type] || '#64748b';
    const row = mk('div', { class: `srow${isShared ? ' srow-locked' : ''}` });

    // drag reorder
    row.draggable = !isShared;
    row.addEventListener('dragstart', e => {
      ST.dragSrcIdx = idx;
      row.classList.add('dragging');
      e.dataTransfer.effectAllowed = 'move';
    });
    row.addEventListener('dragend', () => {
      row.classList.remove('dragging');
      c.querySelectorAll('.drop-above,.drop-below').forEach(r =>
        r.classList.remove('drop-above', 'drop-below'),
      );
    });
    row.addEventListener('dragover', e => {
      e.preventDefault();
      if (ST.dragSrcIdx === idx) {
        return;
      }
      c.querySelectorAll('.drop-above,.drop-below').forEach(r =>
        r.classList.remove('drop-above', 'drop-below'),
      );
      const mid = row.getBoundingClientRect().top + row.offsetHeight / 2;
      row.classList.add(e.clientY < mid ? 'drop-above' : 'drop-below');
    });
    row.addEventListener('dragleave', () => row.classList.remove('drop-above', 'drop-below'));
    row.addEventListener('drop', e => {
      e.preventDefault();
      const cls = Array.from(row.classList);
      row.classList.remove('drop-above', 'drop-below');
      const src = ST.dragSrcIdx;
      if (src === null || src === idx) {
        return;
      }
      const above = cls.includes('drop-above');
      let target = above ? idx : idx + 1;
      if (src < target) {
        target--;
      }
      const moved = ST.sects.splice(src, 1)[0];
      target = Math.max(0, Math.min(target, ST.sects.length));
      ST.sects.splice(target, 0, moved);
      ST.dragSrcIdx = null;
      curPage().edited = false;
      if (ST.activePage === 0) {
        ST.homePageScreenshot = null;
      }
      ST.assembled = assembled(true);
      writeFrame(frameId, ST.assembled);
      renderSList();
      renderSbFoot();
      updSectsCtx();
    });

    // Section row hover → highlight in iframe
    row.addEventListener('mouseenter', () => {
      const el = $(frameId)?.contentDocument?.getElementById(s.id);
      if (el) {
        el.style.outline = '2px dashed rgba(99,102,241,0.5)';
      }
    });
    row.addEventListener('mouseleave', () => {
      const el = $(frameId)?.contentDocument?.getElementById(s.id);
      if (el) {
        el.style.outline = '';
      }
    });

    const main = mk('div', {
      class: 'srow-main',
      onclick(e) {
        if (isShared || e.target.closest('.drag-handle')) {
          return;
        }
        ST.expanded = isExp ? null : idx;
        renderSList();
        renderSbFoot();
      },
    });
    const handle = mk(
      'span',
      {
        class: 'drag-handle',
        title: 'Drag to reorder',
        onmousedown(e) {
          e.stopPropagation();
        },
      },
      '\u2807',
    );
    const dot = mk('div', {
      class:
        'sdot sd-' +
        (s.status === 'done'
          ? 'done'
          : s.status === 'building'
            ? 'spin'
            : s.status === 'error'
              ? 'err'
              : 'idle'),
    });
    if (s.status === 'done') {
      dot.append(
        mk('span', { style: 'font-size:8px;font-weight:700;color:var(--teal)' }, '\u2713'),
      );
    }
    if (s.status === 'error') {
      dot.append(mk('span', { style: 'font-size:8px;font-weight:700;color:var(--red)' }, '!'));
    }

    const body = mk('div', { class: 'srow-body' });
    const meta = mk('div', { class: 'srow-meta' });
    meta.append(
      mk('span', { class: 'sect-pill', style: `background:${tclr}20;color:${tclr}` }, s.type),
    );
    meta.append(
      mk(
        'span',
        {
          class: 'srow-title',
          style: `color:${s.status === 'done' ? 'var(--txt)' : 'var(--txt2)'}`,
        },
        s.title,
      ),
    );
    // Stale badge
    if (s.stale) {
      meta.append(
        mk(
          'span',
          {
            class: 'stale-badge',
            title: 'Design vision changed \u2014 rebuild to apply new direction',
          },
          '\u21bb stale',
        ),
      );
    }
    body.append(meta);

    const lastEdit = s.note || s.editLog?.at(-1)?.instr;
    if (lastEdit) {
      const nt = mk('div', { class: 'srow-note' });
      nt.textContent = `\u21b3 ${lastEdit}`;
      body.append(nt);
    }

    if (isShared) {
      main.append(
        handle,
        dot,
        body,
        mk('span', { class: 'srow-lock', title: 'Edited on Home page, auto-synced' }, 'synced'),
      );
    } else {
      const delBtn = mk(
        'button',
        {
          class: 'sect-delete',
          title: 'Remove section',
          onclick(e) {
            e.stopPropagation();
            removeSection(idx, e);
          },
        },
        '\u00d7',
      );
      main.append(
        handle,
        dot,
        body,
        mk('span', { class: 'srow-arrow' }, isExp ? '\u25b2' : '\u25bc'),
        delBtn,
      );
    }
    row.append(main);

    if (isExp) {
      // Pre-warm screenshot cache for done sections (non-blocking)
      if (s.status === 'done' && !s.screenshotCache) {
        captureSection(s.id).catch(() => {});
      }

      const panel = mk('div', { class: 'srow-exp' });
      const purp = mk('div', { class: 'exp-purpose' });
      purp.textContent = s.purpose;
      panel.append(purp);

      const prevEdits = s.editLog || [];
      if (prevEdits.length) {
        const histEl = mk('div', { class: 'exp-hist' });
        prevEdits.forEach((h, i) => {
          const hrow = mk('div', { class: 'exp-hist-item' });
          hrow.textContent = `${i + 1}. ${h.instr}${h.applied ? ` \u2192 ${h.applied}` : ''}`;
          histEl.append(hrow);
        });
        panel.append(
          mk('div', { class: 'exp-lbl' }, `Edit history (${prevEdits.length}):`),
          histEl,
        );
      }

      panel.append(
        mk(
          'div',
          { class: 'exp-lbl' },
          prevEdits.length ? 'Next direction:' : 'Direction (optional):',
        ),
      );
      const ta = mk('textarea', {
        class: 'exp-ta',
        rows: '2',
        placeholder: prevEdits.length
          ? 'e.g. "now make the CTA bigger"'
          : 'e.g. "dark bg, 3 bullet points, emphasise speed"',
      });
      ta.value = s.note || '';
      ta.addEventListener('input', e => {
        ST.sects[idx].note = e.target.value;
      });
      ta.addEventListener('click', e => {
        e.stopPropagation();
      });
      panel.append(ta);

      // Video URL for video sections
      if (s.type === 'video' || s.type === 'video-hero') {
        const vidVal = s.videoEmbed
          ? `https://${s.videoEmbed.provider === 'youtube' ? 'youtu.be/' + s.videoEmbed.id : 'vimeo.com/' + s.videoEmbed.id}`
          : '';
        const vidInp = mk('input', {
          class: 'inp',
          type: 'text',
          placeholder: 'Paste YouTube or Vimeo URL\u2026',
          value: vidVal,
          style: 'margin-top:8px;font-size:13px',
        });
        vidInp.addEventListener('click', e => e.stopPropagation());
        vidInp.addEventListener('input', e => {
          const url = e.target.value.trim();
          if (!url) {
            ST.sects[idx].videoEmbed = null;
            return;
          }
          const parsed = parseVideoUrl(url);
          if (parsed) {
            ST.sects[idx].videoEmbed = {
              provider: parsed.provider,
              id: parsed.id,
              thumbnailUrl: parsed.thumbnailUrl || '',
            };
          }
        });
        panel.append(
          mk('div', { class: 'exp-lbl', style: 'margin-top:8px' }, 'Video URL:'),
          vidInp,
        );
      }

      const acts = mk('div', { class: 'exp-acts' });
      if (['done', 'error'].includes(s.status)) {
        acts.append(
          mk(
            'button',
            {
              class: 'btn btn-g btn-sm',
              onclick(e) {
                e.stopPropagation();
                ST.expanded = null;
                regenSect(idx);
              },
            },
            '\u21bb Redo',
          ),
        );
      }
      if (['pending', 'error'].includes(s.status)) {
        acts.append(
          mk(
            'button',
            {
              class: 'btn btn-p btn-sm',
              onclick(e) {
                e.stopPropagation();
                ST.expanded = null;
                buildOne(idx);
              },
            },
            '\u25b6 Build now',
          ),
        );
      }
      panel.append(acts);
      row.append(panel);
    }
    c.append(row);
  });
}

/** Render the sidebar footer — build-all / pause buttons. */
function renderSbFoot() {
  const c = $('sb-foot');
  if (!c) {
    return;
  }
  c.innerHTML = '';
  const progress = getProgressInfo(curPage(), ST.activePage > 0);
  const { pending, allDone } = progress;
  const buildable = progress.total;

  if (!allDone && ST.running) {
    c.append(
      mk(
        'button',
        {
          class: 'bld-btn bld-pause',
          onclick() {
            ST.running = false;
            renderSbFoot();
          },
        },
        '\u23f8 Pause',
      ),
    );
  } else if (pending > 0) {
    const b = mk('button', {
      class: 'bld-btn bld-go',
      onclick() {
        startBuildAll();
      },
    });
    b.textContent =
      '\u25b6 Build ' +
      (pending === buildable ? 'all' : `${pending} remaining`) +
      ' section' +
      (pending === 1 ? '' : 's');
    c.append(b);
  }
}

/** Update the progress bar, status text, and export button visibility. */
function updSectsCtx() {
  const pg = curPage();
  const isExtra = ST.activePage > 0;
  const progress = getProgressInfo(pg, isExtra);
  const fill = $('sects-prog');
  if (fill) {
    fill.style.width = `${progress.pct}%`;
  }
  const ctx = $('sects-ctx');
  if (ctx) {
    const name = ST.concept?.siteName || 'site';
    if (pg.shellStatus === 'building') {
      ctx.textContent = 'Building page shell\u2026';
    } else {
      ctx.textContent = getStatusText(progress, name, isExtra);
    }
    // Show/hide export buttons
    const zipBtn = $('topbar-zip');
    const pubBtn = $('topbar-publish');
    if (zipBtn) {
      zipBtn.style.display = progress.canExport ? '' : 'none';
    }
    if (pubBtn) {
      pubBtn.style.display = progress.canExport ? '' : 'none';
    }
  }
}

// ── ZIP / PUBLISH (moved from preview.js) ────────────────────────

/** Bundle all pages into a ZIP file and trigger browser download. */
async function downloadZip() {
  const hasAny = ST.pages.some(p => p.assembled);
  if (!hasAny) {
    showErr('Build at least one page first');
    return;
  }
  if (!window.JSZip) {
    await new Promise((ok, fail) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
      s.onload = ok;
      s.onerror = fail;
      document.head.appendChild(s);
    });
  }
  const zip = new window.JSZip();
  const files = prepareSiteFiles();
  for (const f of files) {
    zip.file(f.path, f.content);
  }
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  const url = URL.createObjectURL(blob);
  const slug = (ST.concept?.siteName || 'site')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  Object.assign(document.createElement('a'), { href: url, download: `${slug}.zip` }).click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** Show the publish modal if at least one page is built. */
function publish() {
  const hasAny = ST.pages.some(p => p.assembled);
  if (!hasAny) {
    showErr('Build at least one page first');
    return;
  }
  showPublishModal();
}

/** Validate Cloudflare credentials and trigger deployment. */
function doPublish() {
  if (!saveCfCreds()) {
    return;
  }
  publishToCloudflare();
}

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export {
  addPageByName,
  addSection,
  doPublish,
  downloadZip,
  publish,
  removePage,
  removeSection,
  renderAddSectBar,
  renderBuild,
  renderBuildTabContent,
  renderBuildTabs,
  renderPageNav,
  renderPrevToolbar,
  renderSbFoot,
  renderSections,
  renderShellRow,
  renderSList,
  switchPage,
  updSectsCtx,
};
